/**
 * Generic filtered and sorted rows for stats dashboard.
 * Works with GenericStatsRow for both drops and lists.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';

import type { GenericStatsRow } from '../../shared/generic-stats.types';
import type { FiltersState } from '../../shared';
import type { GroupByKey } from '../../shared/constants';
import { passesAllFilters } from '../../table/adapters';
import { getVoluumValueForDisplay } from '../../shared';
import { buildGroupsFromRows, type StatsGroupGeneric } from '../../shared/utils/build-groups';

export type { StatsGroupGeneric };

export function useFilteredAndSortedRowsGeneric(
  allRows: GenericStatsRow[],
  filtersState: FiltersState,
  sort: { field: string; dir: 'asc' | 'desc' } | null,
  workDate: string,
  groupBy: GroupByKey,
): {
  filteredRows: GenericStatsRow[];
  rows: GenericStatsRow[];
  groups: StatsGroupGeneric[] | null;
} {
  const filteredRows = useMemo(
    () =>
      allRows.filter((r) =>
        passesAllFilters(r, filtersState.filters, filtersState.active),
      ),
    [allRows, filtersState],
  );

  const { workDayRows, otherRows } = useMemo(() => {
    const workDay: GenericStatsRow[] = [];
    const other: GenericStatsRow[] = [];
    for (const r of filteredRows) {
      if (r.workDateIso === workDate) workDay.push(r);
      else other.push(r);
    }
    return { workDayRows: workDay, otherRows: other };
  }, [filteredRows, workDate]);

  const rows = useMemo(() => {
    if (!sort) return workDayRows.concat(otherRows);
    const { field, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;

    if (field === 'dropList') {
      const bySeq = (a: GenericStatsRow, b: GenericStatsRow) => {
        const seqA = a.sequence ?? 999999;
        const seqB = b.sequence ?? 999999;
        if (seqA !== seqB) return (seqA - seqB) * mult;
        return (a.label ?? '').localeCompare(b.label ?? '') * mult;
      };
      return [...workDayRows].sort(bySeq).concat([...otherRows].sort(bySeq));
    }

    if (['list', 'gateway', 'source', 'text', 'path', 'lander', 'offer'].includes(field)) {
      const byLabel = (a: GenericStatsRow, b: GenericStatsRow) =>
        (a.label ?? a.subLabel ?? '').localeCompare(b.label ?? b.subLabel ?? '') * mult;
      return [...workDayRows].sort(byLabel).concat([...otherRows].sort(byLabel));
    }

    const combined = workDayRows.concat(otherRows);
    const withIndex = combined.map((r, i) => ({ r, i }));
    return withIndex
      .sort((a, b) => {
        const aVal = getVoluumValueForDisplay(a.r.voluum, field);
        const bVal = getVoluumValueForDisplay(b.r.voluum, field);
        const aNum = typeof aVal === 'number' ? aVal : null;
        const bNum = typeof bVal === 'number' ? bVal : null;
        let cmp: number;
        if (aNum != null && bNum != null) {
          cmp = (aNum - bNum) * mult;
        } else if (aNum != null) cmp = -1;
        else if (bNum != null) cmp = 1;
        else cmp = 0;
        if (cmp !== 0) return cmp;
        const idCmp = (a.r.id ?? '').localeCompare(b.r.id ?? '');
        if (idCmp !== 0) return idCmp;
        return a.i - b.i;
      })
      .map((x) => x.r);
  }, [workDayRows, otherRows, sort]);

  const groups = useMemo(
    () => buildGroupsFromRows(rows, groupBy, sort),
    [rows, groupBy, sort],
  );

  return { filteredRows, rows, groups };
}
